<template lang="pug">
section.section(
    :class="classes"
    @click="editSection"
)
    include base.pug
    .content-wrappers(v-if="!isEmpty || $slots.default")
        slot
        template(v-if="!isEmpty")
            .content-wrapper(
                v-for="config in contents"
                v-if="checkConditions(config)"
                :key="config.key"
            )
                template(v-if="config.type === 'html'")
                    .html-content(v-html="config.html")
                media-content(
                    v-if="isSingleAssetConfig(config.media)"
                    :section="section"
                    :config="config"
                    :section-settings="settings"
                    :section-setting-values="settingValues"
                    :edit="false"
                )
                image-content(
                    v-else-if="isSingleAssetConfig(config.image)"
                    :section="section"
                    :config="config"
                    :options="options"
                    :section-settings="settings"
                    :section-setting-values="settingValues"
                    :edit="false"
                )
                input-contents(
                    v-else
                    :section="section"
                    :config="config"
                    :options="options"
                    :section-settings="settings"
                    :section-setting-values="settingValues"
                    :edit="false"
                )
    el-drawer.full-height-drawer.visible-overflow(
        :title="$t('Edit {section}', { section: section.name })"
        :visible.sync="scopedUI.form"
        :close-on-press-escape="true"
        :before-close="confirmDiscard(() => changedSince(drawer.sectionSnapshot, drawer.section))"
        :wrapper-closable="false"
        :append-to-body="true"
        :size="drawerSize"
        :modal="true"
        @closed="resetDrawer"
    )
        .drawer-body(v-loading="drawer.loading")
            template(v-if="tabs === false || !editable")
                .block-settings
                    .settings-wrap(style="padding-top: 0")
                        include settings.pug
                .block-content(v-if="contents && contents.length")
                    include content.pug
            template(v-else)
                el-tabs(
                    v-if="!drawer.loading"
                    v-model="drawer.tab"
                    tab-position="top"
                )
                    el-tab-pane(name="content" :label="$t('Content')")
                        .tab-content(v-if="contents && contents.length")
                            include content.pug
                    el-tab-pane(name="settings" :label="$t('Settings')")
                        .block-settings
                            .settings-wrap
                                include settings.pug
                    el-tab-pane(v-if="hasBannerEditor" name="import-export" :label="$t('Import / Export')")
                        .tab-content.be-import-export-tab
                            .be-ie-section
                                .be-ie-title
                                    i.el-icon-fa-file-export.fas
                                    span {{ $t('Export') }}
                                p.be-ie-desc {{ $t('Saves the entire slider (all slides + layout settings) as a JSON file.') }}
                                el-button(size="small" plain icon="el-icon-fa-download fas" @click="exportSliderData") {{ $t('Download JSON') }}
                            .be-ie-divider
                            .be-ie-section
                                .be-ie-title
                                    i.el-icon-fa-file-import.fas
                                    span {{ $t('Import') }}
                                p.be-ie-desc {{ $t('Replaces the entire slider with data from a previously exported JSON file.') }}
                                el-button(size="small" plain icon="el-icon-fa-upload fas" @click="triggerSliderImport") {{ $t('Select JSON file') }}
                                input(ref="sliderImportInput" type="file" accept=".json" style="display:none" @change="onSliderImportFile")
        .drawer-footer
            include section-drawer-footer.pug
</template>

<i18n>
{
    "en": {
        "Edit {section}": "Edit {section}",
        "Edit grid": "Edit grid",
        "Content": "Content",
        "Settings": "Settings",
        "Grid": "Grid",
        "from": "from",
        "to": "to",
        "Margin top": "Margin top",
        "Margin bottom": "Margin bottom",
        "none": "None",
        "small": "Small",
        "medium": "Medium",
        "large": "Large",
        "default": "Default",
        "custom": "Custom",
        "Everybody": "Everybody",
        "Members": "Members",
        "Guests": "Guests",
        "Any": "Any",
        "Tags": "Tags",
        "Select tags": "Select tags",
        "Accessible by": "Accessible by",
        "desktop": "Desktop",
        "tablet": "Tablet",
        "mobile": "Phone",
        "Visible on": "Visible on",
        "Element is displayed on": "Element is displayed on",
        "Meta Description": "Meta Description",
        "meta_description_tooltip": "This text is used by the AI assistant (chatbot) to understand the content of this section. Especially important for sections without text content such as image galleries, sliders, videos, or product lists. Briefly describe what is shown here — e.g. 'Image gallery with photos from our events' or 'Overview of our most popular products'.",
        "Import / Export": "Import / Export",
        "Export": "Export",
        "Saves the entire slider (all slides + layout settings) as a JSON file.": "Saves the entire slider (all slides + layout settings) as a JSON file.",
        "Download JSON": "Download JSON",
        "Import": "Import",
        "Replaces the entire slider with data from a previously exported JSON file.": "Replaces the entire slider with data from a previously exported JSON file.",
        "Select JSON file": "Select JSON file",
        "Slider imported successfully.": "Slider imported successfully.",
        "Invalid JSON file.": "Invalid JSON file."
    },
    "de": {
        "Edit {section}": "{section} bearbeiten",
        "Edit grid": "Raster bearbeiten",
        "Content": "Inhalt",
        "Settings": "Einstellungen",
        "Grid": "Raster",
        "from": "ab",
        "to": "bis",
        "Margin top": "Abstand oben",
        "Margin bottom": "Abstand unten",
        "none": "Ohne",
        "small": "Klein",
        "medium": "Mittel",
        "large": "Groß",
        "default": "Standard",
        "custom": "Benutzerdefiniert",
        "Everybody": "Jedem",
        "Members": "Mitgliedern",
        "Guests": "Gästen",
        "Any": "Alle",
        "Tags": "Tags",
        "Select tags": "Tags auswählen",
        "Accessible by": "Zugänglich von",
        "desktop": "Desktop",
        "tablet": "Tablet",
        "mobile": "Telefon",
        "Visible on":"Anzeigen auf",
        "Element is displayed on":"Inhaltselement <br> wird dargestellt auf",
        "Meta Description": "Meta-Beschreibung",
        "meta_description_tooltip": "Dieser Text wird vom KI-Assistenten (Chatbot) verwendet, um den Inhalt dieses Abschnitts zu verstehen. Besonders wichtig für Abschnitte ohne Textinhalt wie Bildergalerien, Slider, Videos oder Produktlisten. Beschreiben Sie kurz, was hier zu sehen ist — z.B. \"Bildergalerie mit Fotos unserer Veranstaltungen\" oder \"Übersicht unserer beliebtesten Produkte\".",
        "Import / Export": "Import / Export",
        "Export": "Export",
        "Saves the entire slider (all slides + layout settings) as a JSON file.": "Speichert den gesamten Slider (alle Folien & Layout-Einstellungen) als JSON-Datei.",
        "Download JSON": "JSON herunterladen",
        "Import": "Import",
        "Replaces the entire slider with data from a previously exported JSON file.": "Ersetzt den gesamten Slider mit den Daten einer zuvor exportierten JSON-Datei.",
        "Select JSON file": "JSON-Datei auswählen",
        "Slider imported successfully.": "Slider erfolgreich importiert.",
        "Invalid JSON file.": "Ungültige JSON-Datei."
    }
}
</i18n>

<script>

import _ from 'lodash'
import { getUnixTime, fromUnixTime, compareAsc, compareDesc, addDays, startOfDay } from 'date-fns'
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone'

import router from '@/router'
import store from '@/store'
import { useSetupStore, usePageStore, useSettingsStore } from '@/stores'

import InputField from './Partials/InputField'

import InputContents from './Partials/InputContents'
import MediaContent from './Partials/MediaContent'
import ImageContent from './Partials/ImageContent'

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css'
import discardGuard from '@/mixins/discardGuard'

export default {
    name: 'Section',
    mixins: [discardGuard],
    props: {
        // module: {
        //     type: String,
        //     required: true
        // },
        page: {
            type: Object,
            required: true
        },
        section: {
            type: Object,
            required: true
        },
        sectionConfig: {
            type: Object,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        sortable: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        saveable: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        supported: {
            type: Boolean,
            default: true
        },
        edit: {
            type: Boolean,
            default: false
        },
        hide: {
            type: [Array, Boolean],
            default: false
        },
    },
    data() {
        return {
            outline: 'none',
            tabs: true,
            scopedUI: {
                form: false,
                settings: false,
            },
            drawer: {
                changed: false,
                loading: false,
                // the edited copy as it was when the drawer opened or last applied
                sectionSnapshot: undefined,
                // saving: false,
                tab: 'content',
                tabs: ['content', 'settings'],
                section: undefined,
            },
            restrictions: [
                {
                    key: 'everybody',
                    value: null,
                    name: this.$t('Everybody')
                },
                {
                    key: 'members',
                    value: 'members',
                    name: this.$t('Members')
                },
                {
                    key: 'guests',
                    value: 'guests',
                    name: this.$t('Guests')
                },
            ],
            // responsiveViewports: [
            //     {
            //         key: 'sm',
            //         value: 'sm',
            //         group: 'mobile',
            //         icon: 'el-icon-fa-mobile fas',
            //         name: this.$t('Phone')
            //     },
            //     {
            //         key: 'lg',
            //         value: 'lg',
            //         group: 'tablet',
            //         icon: 'el-icon-fa-tablet fas',
            //         name: this.$t('Tablet')
            //     },
            //     {
            //         key: 'xl',
            //         value: 'xl',
            //         group: 'desktop',
            //         icon: 'el-icon-fa-desktop fas',
            //         name: this.$t('Desktop')
            //     }
            // ]
        }
    },
    components: {
        InputContents,
        MediaContent,
        ImageContent,
        InputField,
    },
    watch: {
        'drawer.section': {
            deep: true,
            handler(old, fresh) {
                // if (fresh) this.drawer.changed = true
            }
        },
        edit(value) {
            if (value === true) this.sectionEdit()
            else this.dismissDrawer()
        },
        restriction(value) {
            if (value === 'member-tags') {
                const i = this.restrictions.findIndex(el => el.key === 'members')
                if (i >= 0) {
                    this.$set(this.restrictions[i], 'key', 'member-tags')
                    this.$set(this.restrictions[i], 'value', 'member-tags')
                }
            } else {
                const i = this.restrictions.findIndex(el => el.key === 'member-tags')
                if (i >= 0) {
                    this.$set(this.restrictions[i], 'key', 'members')
                    this.$set(this.restrictions[i], 'value', 'members')
                }
            }
        }
    },
    computed: {
        isGlobalContentsMode() {
            return usePageStore().isGlobalContentsMode
        },
        globalContentsPositions() {
            if (!this.isGlobalContentsMode) return []
            let settingsStore = useSettingsStore()
            return _.get(settingsStore.form, 'config.template.globalContents.positions') || []
        },
        classes() {
            return [
                {
                    active: this.edit,
                    deleted: this.section.deleted,
                    disabled: !this.status,
                    clickable: !this.disabled,
                    passive: this.disabled,
                },
                `outline-${this.outline}`,
            ]
        },
        marginOptions() {
            return [
                {
                    key: 'none',
                    value: this.$t('none'),
                },
                {
                    key: 'small',
                    value: this.$t('small'),
                },
                {
                    key: 'medium',
                    value: this.$t('medium'),
                },
                {
                    key: 'large',
                    value: this.$t('large'),
                },
                {
                    key: 'default',
                    value: this.$t('default'),
                },
                {
                    key: 'custom',
                    value: this.$t('custom'),
                },
            ]
        },
        marginsFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'section-margins');
        },
        // edit() {
        //     let edit = false

        //     if (this.editable) {
        //         // if (this.module === 'menu' && this.i >= 0 && this.s >= 0) {
        //         //     edit = _.get(this.ui.form, `menuItems.${this.i}.page.sections.${this.s}.edit`, false)
        //         // } else if (this.module === 'categoryPage' && this.s >= 0) {
        //         //     edit = _.get(this.ui.form, `sections.${this.s}.edit`, false)
        //         // }

        //         edit = this.scopedUI.form
        //     }

        //     return edit
        // },
        responsiveViewports() {
            let responsiveViewports = []
            const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || []

            breakpoints.forEach((breakpoint) => {
                if (breakpoint.group == null) breakpoint.group = breakpoint.key
            })

            let groups = breakpoints.map((breakpoint) => breakpoint.group)
                .filter((group, index, self) => self.indexOf(group) === index)

            groups.forEach((group) => {
                let groupKeys = breakpoints.filter((el) => el.group === group).map((el) => el.key)
                let selectedKeys = this.selectedResponsiveKeys

                let checked = groupKeys.filter(el => selectedKeys.includes(el)).length == groupKeys.length
                let indeterminate = groupKeys.filter(el => selectedKeys.includes(el)).length > 0 && groupKeys.filter(el => selectedKeys.includes(el)).length < groupKeys.length

                responsiveViewports.push({
                    key: group,
                    label: group,
                    keys: groupKeys,
                    checked,
                    indeterminate
                })
            })

            return responsiveViewports
        },
        editable() {
            return !!this.contents.length;
        },
        drawerSize() {
            const wideKeys = ['slider-v2']
            const isWide = wideKeys.includes(this.sectionConfig && this.sectionConfig.key)
                || this.hasFormBuilder
            if (isWide) {
                const vw = typeof window !== 'undefined' ? window.innerWidth : 1400
                return Math.min(1400, vw) + 'px'
            }
            return '920px'
        },
        hasBannerEditor() {
            return this.contents.some(c =>
                (c.fields || []).some(f => f.type === 'banner-editor')
            )
        },
        hasFormBuilder() {
            return this.contents.some(c =>
                (c.fields || []).some(f => f.type === 'form-builder')
            )
        },
        bannerEditorContent() {
            const config = this.contents.find(c =>
                (c.fields || []).some(f => f.type === 'banner-editor')
            )
            if (!config) return null
            const sectionContents = _.get(this.drawer.section, 'contents') || []
            return sectionContents.find(c => c.key === config.key) || null
        },
        defaultConfig() {
            const sections = _.get(this.activeCms, 'config.template.sections', [])
            return sections.find(section => section.key === this.section.key)
        },
        settings() {
            let settings = _.get(this.sectionConfig, 'settings')
                || _.get(this.defaultConfig, 'settings') || []

            // make divider keys unique
            settings = _.cloneDeep(settings)
            settings.forEach((setting, index) => {
                if (setting.type === 'divider') {
                    setting.key = `${setting.key}-${index}`
                }
            })

            return settings;
        },
        sectionSettingValues() {
            const settings = _.get(this.section, 'settings') || []
            return this.getSettingValues(settings)
        },
        drawerSettingValues() {
            const settings = _.get(this.drawer.section, 'settings') || []
            return this.getSettingValues(settings)
        },
        settingValues() {
            const settings = _.get(this.drawer.section, 'settings') || _.get(this.section, 'settings') || []
            return this.getSettingValues(settings)
        },
        contents() {
            let contents = []

            const sectionContents = _.get(this.sectionConfig, 'contents') || []
            const defaultContents = _.get(this.defaultConfig, 'contents') || []

            if (sectionContents.length) {
                sectionContents.forEach(content => {
                    content = _.cloneDeep(content)
                    if (!content.fields) {
                        const defaultContent = defaultContents.find(el => el.key === content.key)
                        if (defaultContent && defaultContent.fields) {
                            content.fields = defaultContent.fields
                        }
                    }
                    if (!content.settings) {
                        const defaultContent = defaultContents.find(el => el.key === content.key)
                        if (defaultContent && defaultContent.settings) {
                            content.settings = defaultContent.settings
                        }
                    }
                    contents.push(content)
                })
            } else {
                contents = defaultContents
            }

            return _.cloneDeep(contents)
                .sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))
                .filter(content => this.checkConditions(content))
        },
        isEmpty() {
            // let empty = !this.$slots.default
            let empty = true

            // if (empty && this.section.categories && this.section.categories.length) empty = false
            // if (empty && this.section.tags && this.section.tags.length) empty = false

            // if (empty && this.section.informations && this.section.informations.length) empty = false
            // if (empty && this.section.eventCategories && this.section.eventCategories.length) empty = false
            // if (empty && this.section.shopCategories && this.section.shopCategories.length) empty = false
            // if (empty && this.section.products && this.section.products.length) empty = false

            const contents = _.get(this.section, 'contents') || []
            contents.forEach(content => {
                if (! empty) return
                if (content.addresses && content.addresses.length) empty = false
                if (content.images && content.images.length) empty = false
                if (content.media && content.media.length) empty = false
                if (content.title || content.sub_title || content.text) empty = false
            })

            return empty
        },
        // i() { // item key
        //     let index = null

        //     if (this.module === 'menu') {
        //         const menuItems = _.get(this.form, 'menuItems', [])
        //         const currentID = _.get(router, 'history.current.params.id')

        //         if (menuItems.length && currentID) {
        //             index = _.findIndex(menuItems, item => item.id.toString() === currentID.toString())
        //         }
        //     }

        //     return index
        // },
        // s() { // section key
        //     let index = null
        //     let sections = []

        //     if (this.module === 'menu' && this.i !== null && this.section.id) {
        //         sections = _.get(this.form.menuItems, `${this.i}.page.sections`, [])
        //     } else if (this.module === 'categoryPage' && this.section.id) {
        //         sections = _.get(this.form, 'sections', [])
        //     }

        //     if (this.section.id && sections.length) {
        //         const currentID = this.section.id
        //         index = _.findIndex(sections, item => item.id.toString() === currentID.toString())
        //     }

        //     return index
        // },
        // form() {
        //     return store.getters[`${this.module}/form`]
        // },
        // ui() {
        //     return store.getters[`${this.module}/ui`]
        // },
        // options() {
        //     return store.getters[`${this.module}/options`]
        // },
        status: {
            get() {
                let enabled = true

                const start = this.publishStart
                if (start && enabled) enabled = compareAsc(new Date(), start) > 0

                const end = this.publishEnd
                // if (end && enabled) enabled = compareDesc(new Date(), addDays(end, 1)) > 0
                if (end && enabled) enabled = compareDesc(new Date(), end) > 0

                return enabled
            },
            set(active) {

                const start = this.publishStart
                const end = this.publishEnd

                if (active) {
                    if (start && compareAsc(new Date(), start) < 0) this.publishStart = startOfDay(new Date())
                    else if (end && compareDesc(new Date(), end) < 0) this.publishEnd = null
                } else {
                    this.publishEnd = startOfDay(new Date())
                }
            }
        },
        publishStart: {
            get() {
                const section = this.drawer.section || this.section
                const start =  _.get(section, 'publish_start')
                return start ? startOfDay(appFromUnixTime(start)) : ''
            },
            set(value) {
                const section = this.drawer.section || this.section
                const start = value !== null ? appGetUnixTime(value) : null
                // store.commit(`${this.module}/form/sectionPublishStart`, { i: this.i, s: this.s, value: start })
                this.$set(section, 'publish_start', start);
            }
        },
        publishEnd: {
            get() {
                const section = this.drawer.section || this.section
                const end = _.get(section, 'publish_end')
                return end ? startOfDay(appFromUnixTime(end)) : ''
            },
            set(value) {
                const section = this.drawer.section || this.section
                const end = value !== null ? appGetUnixTime(value) : null
                // store.commit(`${this.module}/form/sectionPublishEnd`, { i: this.i, s: this.s, value: end })
                this.$set(section, 'publish_end', end);
            }
        },
        metaDescription: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'meta_description') || ''
            },
            set(value) {
                const section = this.drawer.section || this.section
                this.$set(section, 'meta_description', value || null)
            },
        },
        selectedResponsiveKeys() {
            const section = this.drawer.section || this.section

            const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || []
            let defaults = breakpoints.map(breakpoint => ({ key: breakpoint.key, visibility: true }))

            let selectedKeys = _.get(section, 'responsive') || defaults
            selectedKeys = selectedKeys.filter((el) => el.visibility).map((el) => el.key)

            return selectedKeys
        },
        responsive: {
            get() {
                const section = this.drawer.section || this.section

                const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || []
                const selectedKeys = this.selectedResponsiveKeys

                breakpoints.forEach((breakpoint) => {
                    if (breakpoint.group == null) breakpoint.group = breakpoint.key
                })

                let groups = breakpoints.map((breakpoint) => breakpoint.group)
                    .filter((group, index, self) => self.indexOf(group) === index)

                // group selected/default keys

                let checkboxes = []
                groups.forEach((group) => {
                    let groupKeys = breakpoints.filter((el) => el.group === group).map((el) => el.key)
                    if (groupKeys.filter(el => selectedKeys.includes(el)).length > 0) {
                        checkboxes.push(group)
                    }
                })

                return checkboxes
            },
            set(groups) {
                const section = this.drawer.section || this.section

                const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || []
                breakpoints.forEach((breakpoint) => {
                    if (breakpoint.group == null) breakpoint.group = breakpoint.key
                })

                let activeKeys = []
                groups.forEach((group) => {
                    let groupKeys = breakpoints.filter((el) => el.group === group).map((el) => el.key)
                    activeKeys = activeKeys.concat(groupKeys)
                })

                // const responsive = breakpoints.map((el) => ({ key: el.key, visibility: activeKeys.includes(el.key) }))

                let responsive = section.responsive || []
                breakpoints.forEach((el) => {
                    let existing = responsive.find((item) => item.key === el.key)
                    if (existing) {
                        existing.visibility = activeKeys.includes(el.key)
                    } else if (activeKeys.includes(el.key)) {
                        responsive.push({ key: el.key, visibility: true })
                    }
                })

                this.$set(section, 'responsive', responsive);
            }
        },
        restriction: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'restriction')
            },
            set(value) {
                const section = this.drawer.section || this.section
                this.$set(section, 'restriction', value);
            }
        },
        restrictionMemberTags: {
            get() {
                const section = this.drawer.section || this.section
                let tags = _.get(section, 'restrictionMemberTags');
                return tags.map(tag => {
                    if (tag.id.startsWith('new:')) return tag.name
                    else return tag
                })
            },
            set(tags) {
                const section = this.drawer.section || this.section

                let memberTags = []
                tags.forEach(tag => {
                    if (typeof tag === 'string' || tag instanceof String) {
                        memberTags.push({
                            id: uniqid('new:'),
                            name: tag,
                        })
                    } else {
                        memberTags.push(tag)
                    }
                })

                this.$set(section, 'restrictionMemberTags', memberTags);
            }
        },
        marginTop: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'margin_top') || 'default'
            },
            set(value) {
                const section = this.drawer.section || this.section
                // store.commit(`${this.module}/form/marginTop`, { i: this.i, s: this.s, value })
                this.$set(section, 'margin_top', value);
            }
        },
        marginTopCustomValue: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'margin_top_custom_value')
            },
            set(value) {
                const section = this.drawer.section || this.section
                // store.commit(`${this.module}/form/marginTopCustomValue`, { i: this.i, s: this.s, value })
                this.$set(section, 'margin_top_custom_value', value);
            }
        },
        // marginTopCustomTabletValue: {
        //     get() {
        //         const section = this.drawer.section || this.section
        //         return _.get(section, 'margin_top_custom_tablet_value')
        //     },
        //     set(value) {
        //         const section = this.drawer.section || this.section
        //         // store.commit(`${this.module}/form/marginTopCustomValue`, { i: this.i, s: this.s, value })
        //         this.$set(section, 'margin_top_custom_tablet_value', value);
        //     }
        // },
        // marginTopCustomMobileValue: {
        //     get() {
        //         const section = this.drawer.section || this.section
        //         return _.get(section, 'margin_top_custom_mobile_value')
        //     },
        //     set(value) {
        //         const section = this.drawer.section || this.section
        //         // store.commit(`${this.module}/form/marginTopCustomValue`, { i: this.i, s: this.s, value })
        //         this.$set(section, 'margin_top_custom_mobile_value', value);
        //     }
        // },
        marginBottom: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'margin_bottom') || 'default'
            },
            set(value) {
                const section = this.drawer.section || this.section
                // store.commit(`${this.module}/form/marginBottom`, { i: this.i, s: this.s, value })
                this.$set(section, 'margin_bottom', value);
            }
        },
        marginBottomCustomValue: {
            get() {
                const section = this.drawer.section || this.section
                return _.get(section, 'margin_bottom_custom_value')

            },
            set(value) {
                const section = this.drawer.section || this.section
                // store.commit(`${this.module}/form/marginBottomCustomValue`, { i: this.i, s: this.s, value })
                this.$set(section, 'margin_bottom_custom_value', value);
            }
        },
        // marginBottomCustomTabletValue: {
        //     get() {
        //         const section = this.drawer.section || this.section
        //         console.log(_.get(section, 'margin_bottom_custom_tablet_value'))
        //         return _.get(section, 'margin_bottom_custom_tablet_value')
        //     },
        //     set(value) {
        //         const section = this.drawer.section || this.section
        //         // store.commit(`${this.module}/form/marginBottomCustomValue`, { i: this.i, s: this.s, value })
        //         this.$set(section, 'margin_bottom_custom_tablet_value', value);
        //     }
        // },
        // marginBottomCustomMobileValue: {
        //     get() {
        //         const section = this.drawer.section || this.section
        //         return _.get(section, 'margin_bottom_custom_mobile_value')
        //     },
        //     set(value) {
        //         const section = this.drawer.section || this.section
        //         // store.commit(`${this.module}/form/marginBottomCustomValue`, { i: this.i, s: this.s, value })
        //         this.$set(section, 'margin_bottom_custom_mobile_value', value);
        //     }
        // },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions
        },
        // Member restriction is functionality, not a sold row - a shop carries it
        // without the restricted-area extension ever being attached.
        effectiveExtensions() {
            return this.setupStore.effectiveExtensions
        },
        globalSettingValues() {
            return this.setupStore.settingValues
        },
        copiedSection() {
            var cmsId = _.get(this.activeCms, 'id');
            if (!cmsId) return null;
            return _.get(this.setupStore.clipboard, 'cms:' + cmsId + '.section');
        },
        isCopied() {
            return this.copiedSection && this.copiedSection.sectionId === this.section.id;
        },
    },
    methods: {
        updateGlobalContentsPosition(position) {
            this.section.area = position
            if (this.section.id && !String(this.section.id).startsWith('new:')) {
                this.$emit('save', { ...this.section, area: position })
            }
        },
        toNumericValue(value) {
            if (value === null || value === undefined || value === '') return null

            const numericValue = Number(value)
            return Number.isNaN(numericValue) ? null : numericValue
        },
        isSingleAssetConfig(assetConfig) {
            if (!assetConfig) return false

            return this.toNumericValue(_.get(assetConfig, 'min')) === 1
                && this.toNumericValue(_.get(assetConfig, 'max')) === 1
        },
        isSingleContentConfig(config) {
            return this.toNumericValue(_.get(config, 'max')) === 1 && this.contents.length === 1
        },
        editSection() {
            if (!this.disabled) {
                this.$emit('edit', this.section)
            }
        },
        getSettingValues(settings) {
            let settingValues = {}
            let options = {}

            // select fields
            // get default values
            // so that invalid values can be determined
            if (this.settings && this.settings.length) {
                this.settings.forEach(setting => {
                    switch (setting.type) {
                        case 'select:searchable-properties':
                        case 'multi-select:searchable-properties':
                            if (setting.options && setting.options.length) {
                                setting.options.forEach(option => {
                                    if (! settingValues[setting.key]) settingValues[setting.key] = []
                                    const properties = this.options.extensions.shop.property.items.filter(el => !!el.use_in_search)
                                    properties.forEach(property => {
                                        if ((option.key == null && property.filter_component == null) || (property.filter_component === option.key)) {
                                            settingValues[setting.key].push({ key: property.key, value: property.name })
                                            _.set(options, `${setting.key}.${property.key}`, property.name)
                                        }
                                    })
                                })
                            } else {
                                if (! settingValues[setting.key]) settingValues[setting.key] = []
                                const properties = this.options.extensions.shop.property.items.filter(el => !!el.use_in_search)
                                properties.forEach(property => {
                                    settingValues[setting.key].push({ key: property.key, value: property.name })
                                    _.set(options, `${setting.key}.${property.key}`, property.name)
                                })
                            }
                            break;
                        case 'select:sortable-properties':
                        case 'multi-select:sortable-properties':
                            if (! settingValues[setting.key]) settingValues[setting.key] = []
                            const properties = this.options.extensions.shop.property.items.filter(el => !!el.is_sortable)
                            properties.forEach(property => {
                                settingValues[setting.key].push({ key: property.key, value: property.name })
                                _.set(options, `${setting.key}.${property.key}`, property.name)
                            })
                            break;
                        case 'multi-select:property-values':
                            // options load on demand per property, so there is no list to validate against
                            settingValues[setting.key] = []
                            break;
                        case 'select:properties':
                            // nothing is picked until the editor picks one; the map only validates stored keys
                            settingValues[setting.key] = []
                            this.options.extensions.shop.property.items.forEach(property => {
                                _.set(options, `${setting.key}.${property.key}`, property.name)
                            })
                            break;
                        case 'select:subscription-topics':
                        case 'multi-select:subscription-topics':
                            if (! settingValues[setting.key]) settingValues[setting.key] = []
                            const subscriptionTopics = this.options.extensions.newsletter.subscriptionTopic.items
                            subscriptionTopics.forEach(topic => {
                                settingValues[setting.key].push({ key: topic.key, value: topic.name })
                                _.set(options, `${setting.key}.${topic.key}`, topic.name)
                            })
                            break;
                        case 'select:member-tags':
                        case 'multi-select:member-tags':
                            if (! settingValues[setting.key]) settingValues[setting.key] = []
                            const memberTags = this.options.extensions.restrictedArea.memberTag.items
                            memberTags.forEach(memberTag => {
                                // settingValues[setting.key].push({ key: memberTag.key, value: memberTag.name })
                                _.set(options, `${setting.key}.${memberTag.key}`, memberTag.name)
                            })
                            break;
                        case 'checkbox':
                        case 'switch':
                            settingValues[setting.key] = false
                            if (setting.value) {
                                settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                            }
                            break;

                        default:
                            if (setting.options && setting.options.length) {
                                setting.options.forEach(option => {
                                    if (option.default === true && ! settings.find(el => el.key === setting.key)) {
                                        if (['multi-select', 'tags'].includes(setting.type)) {
                                            if (! settingValues[setting.key]) settingValues[setting.key] = []
                                            settingValues[setting.key].push({ key: option.key, value: option.value })
                                        } else {
                                            settingValues[setting.key] = option.key
                                        }
                                    }
                                    _.set(options, `${setting.key}.${option.key}`, option.value)
                                })
                            } else {
                                settingValues[setting.key] = setting.value
                            }
                            break
                    }
                })
            }

            // override with actual values
            if (settings && settings.length) {
                settings.forEach(setting => {
                    // check if selected option exists

                    // get related configuration (defined in temaplte JSON)
                    const config = this.settings.find(el => el.key === setting.key)
                    const type = _.get(config, 'type')

                    switch (type) {
                        case 'tags':
                        case 'select':
                        case 'multi-select':
                        case 'select:member-tags':
                        case 'multi-select:member-tags':
                        case 'select:subscription-topics':
                        case 'multi-select:subscription-topics':
                            if (setting.key && setting.value != null && _.get(options, `${setting.key}.${setting.value}`) !== undefined) {
                                settingValues[setting.key] = setting.value
                            } else if (setting.options && setting.options.length) {
                                settingValues[setting.key] = setting.options
                            }
                            break;

                        case 'multi-select:property-values':
                            if (setting.key && setting.options) settingValues[setting.key] = setting.options
                            break;

                        case 'select:searchable-properties':
                        case 'multi-select:searchable-properties':
                        case 'select:sortable-properties':
                        case 'multi-select:sortable-properties':
                        case 'select:properties':
                            if (setting.key && setting.options) {
                                let values = []
                                setting.options.forEach(option => {
                                    if (option.key && option.value != null && _.get(options, `${setting.key}.${option.key}`) !== undefined) {
                                        values.push(option)
                                    }
                                })
                                settingValues[setting.key] = values
                            }

                            // if (setting.options && setting.options.length) {
                            //     settingValues[setting.key] = setting.options
                            // } else if (setting.options === null) {
                            //     settingValues[setting.key] = []
                            // }
                            break;

                        case 'checkbox':
                        case 'switch':
                            settingValues[setting.key] = false
                            if (setting.value) {
                                settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                            }
                            break;

                        default:
                            settingValues[setting.key] = setting.value
                    }
                })
            }

            return settingValues
        },
        checkConditions(setting, scope = 'local') {
            let valid = true

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            const enabledExtensions = this.enabledExtensions;
            try {
                const conditions = setting.conditions
                if (conditions && conditions.length) {
                    conditions.forEach(o => {
                        if (valid === false) return

                        const { type, condition } = o
                        switch (type) {
                            case 'eval':
                                valid = eval(condition)
                                break;

                            default:
                                valid = false
                        }
                    })
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false
            }

            return valid
        },
        sectionEdit(event) {
            if (this.scopedUI.form === true || this.disabled || this.section.deleted) return false
            // store.commit(`${this.module}/setUiAllSectionsEdit`, { i: this.i, value: false })
            // store.commit(`${this.module}/setUiSectionEdit`, { i: this.i, s: this.s, value: true })

            this.drawer.section = _.cloneDeep(this.section)
            this.drawer.sectionSnapshot = this.snapshotOf(this.drawer.section)
            this.scopedUI.form = true
        },
        sectionApply() {
            // prevent deleting new sections
            this.drawer.changed = true

            const section = _.cloneDeep(this.drawer.section)
            this.drawer.sectionSnapshot = this.snapshotOf(section)
            this.$nextTick(() => this.$emit('apply', section))
        },
        sectionSave() {
            // prevent deleting new sections
            this.drawer.changed = true

            const section = _.cloneDeep(this.drawer.section)
            this.drawer.sectionSnapshot = this.snapshotOf(section)
            this.$nextTick(() => this.$emit('save', section))
        },
        dismissDrawer() {
            if (this.scopedUI.settings) {
                this.scopedUI.settings = false
                // setTimeout(() => {
                //     store.commit(`${this.module}/setUiSectionEdit`, { i: this.i, s: this.s, value: false })
                // }, 300)
            } else {
                // store.commit(`${this.module}/setUiSectionEdit`, { i: this.i, s: this.s, value: false })
            }

            this.scopedUI.form = false
        },
        exportSliderData() {
            const content = this.bannerEditorContent
            const json = content && content.text ? content.text : '{}'
            const blob = new Blob([json], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'slider-export.json'
            a.click()
            URL.revokeObjectURL(url)
        },
        triggerSliderImport() {
            this.$refs.sliderImportInput.value = ''
            this.$refs.sliderImportInput.click()
        },
        onSliderImportFile(e) {
            const file = e.target.files[0]
            if (!file) return
            const reader = new FileReader()
            reader.onload = (ev) => {
                try {
                    const data = JSON.parse(ev.target.result)
                    if (!data || typeof data !== 'object') throw new Error()
                    const content = this.bannerEditorContent
                    if (content) {
                        this.$set(content, 'text', ev.target.result)
                        this.drawer.changed = true
                    }
                    this.$message({ type: 'success', message: this.$t('Slider imported successfully.') })
                    this.drawer.tab = 'content'
                } catch {
                    this.$message({ type: 'error', message: this.$t('Invalid JSON file.') })
                }
            }
            reader.readAsText(file)
        },
        resetDrawer() {
            this.$emit('cancel')

            // check if drawer section has been saved
            // if not, remove from state

            if (!this.drawer.changed && _.startsWith(this.drawer.section.id, 'new:')) {
                this.$nextTick(() => {
                    if (this.isEmpty) this.deleteSection()
                })
            }

            this.drawer.changed = false
            this.drawer.loading = false
            // this.drawer.saving = false
            this.drawer.tab = 'content'
            this.drawer.section = undefined
        },
        toggleMemberRestriction(value) {
            if (value === true) this.restriction = 'members'
            else this.restriction = 'member-tags'
        },
        toggleSettings() {
            this.scopedUI.settings = ! this.scopedUI.settings
        },
        deleteSection() {
            // store.commit(`${this.module}/form/markSectionAsDeleted`, { i: this.i, s: this.s })
            this.$emit('delete', this.section)
        },
        restoreSection() {
            // store.commit(`${this.module}/form/unmarkSectionAsDeleted`, { i: this.i, s: this.s })
            this.$emit('restore', this.section)
        },
        copySection() {
            if (this.isCopied) {
                this.setupStore.removeFromClipboard('section');
            } else {
                this.setupStore.setClipboard({
                    key: 'section',
                    value: {
                        sectionId: this.section.id,
                        sectionKey: this.section.key,
                        sectionType: this.section.type,
                        sectionName: this.section.name || this.sectionConfig.name || this.section.key,
                    },
                });
            }
        },
        updateSetting(payload) {
            // let mutation = 'sectionSettingsValue'
            // if (payload.value && _.isArray(payload.value)) {
            //     mutation = 'sectionSettingsOptions'
            //     payload.options = payload.value
            //     delete payload.value
            // }
            // store.commit(`${this.module}/form/${mutation}`, { i: this.i, s: this.s, ...payload })

            const { key, field, value } = payload;

            let k = 'value';
            if (value != null && _.isArray(value)) k = 'options'

            const section = this.drawer.section || this.section
            let settings = _.get(section, 'settings');
            if (!settings) {
                this.$set(section, 'settings', []);
                this.$set(section.settings, 0, { key, [k]: value });
                // settings = [{ key, [k]: value }];
            } else if (settings && settings.length === 0) {
                this.$set(section.settings, 0, { key, [k]: value });
                // settings.push({ key, [k]: value })
            } else {
                const index = _.findIndex(settings, { key });
                if (index >= 0) {
                    this.$set(section.settings, index, { key, [k]: value });
                    // settings[index] = { key, [k]: value }
                } else {
                    this.$set(section.settings, settings.length, { key, [k]: value });
                    // settings.push({ key, [k]: value })
                }
            }

            // this.$set(section, 'settings', settings);
        },

        disabledPublishStartDate(start) {
            const end = this.publishEnd
            if (end) return compareAsc(addDays(start, 1), end) > 0
            else return false
        },
        disabledPublishEndDate(end) {
            const start = this.publishStart
            if (start) return compareDesc(end, addDays(start, 1)) > 0
            else return false
        },
    },
    mounted() {
        if (this.edit === true) this.sectionEdit()
    }
}
</script>
<style lang="sass" scoped>
.viewports
    margin-right: 12px
    .el-tooltip
        margin: 0 3px
        color: #9ca3af
        .inner
            text-align: center

.meta-description-setting
    .comment
        .tooltip
            margin-left: 0.5rem
            padding-top: 0
            .el-button
                padding: 0
                height: 28px
                width: 28px
                line-height: 20px
                text-align: center
                font-weight: 500
                background: #f9fafc
                &:focus, &:active
                    border-color: #dde0e6
                    color: #606266

.be-import-export-tab
    padding: 24px 20px

.be-ie-section
    display: flex
    flex-direction: column
    gap: 8px

.be-ie-title
    display: flex
    align-items: center
    gap: 8px
    font-size: 14px
    font-weight: 600
    color: #374151
    i
        font-size: 15px
        color: #6b7280

.be-ie-desc
    font-size: 13px
    color: #6b7280
    margin: 0

.be-ie-divider
    height: 1px
    background: #e5e7eb
    margin: 20px 0
</style>
